import {
    DesiredCompensationBasis,
    Worker,
    WorkerAvailabilityStatus,
    WorkerStatus,
} from "../../models/worker";

export interface WorkerFormState {
    firstName: string;
    prefix: string;
    middleName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    nationality: string;
    dateOfBirth: string;
    professionKey: string;
    customOccupation: string;
    isCustomOccupation: boolean;
    status: WorkerStatus;
    workerAvailability: WorkerAvailabilityStatus;
    recruiterId: number | null;
    accountManagerId: number | null;
    languages: string[];
    memo: string;
    willingToWorkInNetherlands: boolean | null;
    housingNeeded: boolean | null;
    ownTransport: boolean | null;
    desiredCompensationBasis: DesiredCompensationBasis | null;
    desiredCompensationAmount: number | null;
    originCountry: string;
    originCity: string;
}

export const EMPTY_WORKER_FORM: WorkerFormState = {
    firstName: "",
    prefix: "",
    middleName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    nationality: "",
    dateOfBirth: "",
    professionKey: "",
    customOccupation: "",
    isCustomOccupation: false,
    status: WorkerStatus.INCOMPLETE,
    workerAvailability: WorkerAvailabilityStatus.UNAVAILABLE,
    recruiterId: null,
    accountManagerId: null,
    languages: [],
    memo: "",
    willingToWorkInNetherlands: null,
    housingNeeded: null,
    ownTransport: null,
    desiredCompensationBasis: null,
    desiredCompensationAmount: null,
    originCountry: "",
    originCity: "",
};

const toDateInputValue = (dateString?: string | null): string => dateString?.split("T")[0] ?? "";

export const workerToFormState = (worker: Worker): WorkerFormState => ({
    firstName: worker.firstName || "",
    prefix: worker.prefix || "",
    middleName: worker.middleName || "",
    lastName: worker.lastName || "",
    emailAddress: worker.emailAddress || "",
    phoneNumber: worker.phoneNumber || "",
    nationality: worker.nationality || "",
    dateOfBirth: toDateInputValue(worker.dateOfBirth),
    professionKey: worker.professionKey || "",
    customOccupation: worker.customOccupation || "",
    isCustomOccupation: Boolean(worker.isCustomOccupation),
    status: worker.status || WorkerStatus.INCOMPLETE,
    workerAvailability: worker.workerAvailability || WorkerAvailabilityStatus.UNAVAILABLE,
    recruiterId: worker.recruiterId ?? null,
    accountManagerId: worker.accountManagerId ?? null,
    languages: worker.languages || [],
    memo: worker.memo || "",
    willingToWorkInNetherlands: worker.willingToWorkInNetherlands ?? null,
    housingNeeded: worker.housingNeeded ?? null,
    ownTransport: worker.ownTransport ?? null,
    desiredCompensationBasis: worker.desiredCompensationBasis ?? null,
    desiredCompensationAmount: worker.desiredCompensationAmount ?? null,
    originCountry: worker.homeAddress?.country || "",
    originCity: worker.homeAddress?.city || "",
});

export const recruitmentPreferencesPayload = (
    form: WorkerFormState,
    currentHomeAddress?: Worker["homeAddress"],
) => {
    const homeAddress = {
        street: currentHomeAddress?.street ?? null,
        houseNumber: currentHomeAddress?.houseNumber ?? null,
        postalCode: currentHomeAddress?.postalCode ?? null,
        city: form.originCity.trim() || null,
        country: form.originCountry.trim() || null,
    };
    const hasHomeAddress = Object.values(homeAddress).some((field) => field !== null);

    return {
        willingToWorkInNetherlands: form.willingToWorkInNetherlands,
        housingNeeded: form.housingNeeded,
        ownTransport: form.ownTransport,
        desiredCompensationBasis: form.desiredCompensationBasis,
        desiredCompensationAmount: form.desiredCompensationAmount,
        homeAddress: hasHomeAddress ? homeAddress : null,
    };
};
